
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/context/TranslationContext';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Declare global window properties
declare global {
  interface Window {
    translationTimeout?: NodeJS.Timeout;
    isTranslatingFullPage?: boolean;
  }
}

const ATTRS_TO_TRANSLATE = ['placeholder','title','aria-label','alt'] as const;

const PageObserver: React.FC = () => {
  const { currentLanguage, translate, originalLanguage, staticOnlyMode } = useTranslation();
  const translationErrorCount = useRef(0);
  const processedNodes = useRef(new Set<Node>());
  const [isTranslating, setIsTranslating] = useState(false);
  const previousLanguage = useRef(currentLanguage);
  const generationRef = useRef(0);
  const forceNextCycleRef = useRef(false);
  const translateRef = useRef(translate);
  useEffect(() => { translateRef.current = translate; }, [translate]);

  const disabled = staticOnlyMode;
  // Force full page translation when language changes
  useEffect(() => {
    if (disabled) return;
    if (currentLanguage === previousLanguage.current) return;
    
    console.log(`Language changed from ${previousLanguage.current} to ${currentLanguage}, forcing full page translation`);
    previousLanguage.current = currentLanguage;
    
    // Skip if we're using the original language
    if (currentLanguage === originalLanguage) {
      return;
    }

    // Clear processed nodes record to force retranslation
    processedNodes.current.clear();
    
    // Reset all data-translated attributes to force retranslation
    const translatedElements = document.querySelectorAll('[data-translated="true"]');
    translatedElements.forEach(element => {
      element.removeAttribute('data-translated');
    });

    // Also reset translated attribute markers
    const attrSelector = ATTRS_TO_TRANSLATE.map(a => `[data-translated-attr-${a}="true"]`).join(',');
    if (attrSelector) {
      const translatedAttrElements = document.querySelectorAll(attrSelector);
      translatedAttrElements.forEach(el => {
        ATTRS_TO_TRANSLATE.forEach(a => {
          (el as HTMLElement).removeAttribute(`data-translated-attr-${a}`);
        });
      });
    }

    // Begin full page translation with short delay to allow UI update
    setTimeout(() => {
      window.isTranslatingFullPage = true;
      translateFullPage();
    }, 100);

  }, [currentLanguage, originalLanguage, disabled]);

  // Function to translate all visible text on the page
  const translateFullPage = async (force = false) => {
    // Capture a generation to avoid stale writes
    const genAtStart = ++generationRef.current;
    const langAtStart = currentLanguage;

    // Skip if we're using the original language
    if (currentLanguage === originalLanguage) {
      window.isTranslatingFullPage = false;
      return;
    }

    setIsTranslating(true);
    toast.info('Översätter hela sidan...', { 
      id: 'translating-page',
      duration: 2000
    });
    
    try {
      // Find all visible text nodes that aren't inside do-not-translate elements
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            // Skip empty or whitespace-only nodes
            if (!node.textContent || !node.textContent.trim()) {
              return NodeFilter.FILTER_REJECT;
            }
            
            // Skip nodes inside do-not-translate elements
            let parent = node.parentElement;
            while (parent) {
              if (parent.hasAttribute('data-do-not-translate') && 
                  parent.getAttribute('data-do-not-translate') === "true") {
                return NodeFilter.FILTER_REJECT;
              }
              parent = parent.parentElement;
            }
            
            // Accept visible text nodes
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const nodesToTranslate = [];
      let currentNode;
      
      // Collect all text nodes that need translation
      while (currentNode = walker.nextNode()) {
        // Skip if node's text content is trivial
        if (currentNode.textContent && currentNode.textContent.trim().length > 1) {
          nodesToTranslate.push(currentNode);
        }
      }
      
      // Use variable instead of const for batch size, so we can reduce it if needed
      let maxNodesPerBatch = window.isTranslatingFullPage ? 20 : 5;
      let processedCount = 0;
      let errorCount = 0;
      
      // Process in batches
      while (nodesToTranslate.length > 0) {
        const nodeBatch = nodesToTranslate.splice(0, maxNodesPerBatch);
        
        await Promise.all(nodeBatch.map(async (node) => {
          try {
            if (node.textContent && node.textContent.trim()) {
              // Only translate if it hasn't been processed before
              if (!processedNodes.current.has(node) && !node.parentElement?.hasAttribute('data-translated')) {
                const sourceText = (node.parentElement?.getAttribute('data-original-text') || node.textContent || '').trim();
                
                try {
                  const translatedText = await translateRef.current(sourceText, force);
                  
                  // Stale guard: only apply if generation and language still match
                  if (generationRef.current !== genAtStart || currentLanguage !== langAtStart) {
                    return;
                  }
                  
                  if (sourceText !== translatedText) {
                    node.textContent = translatedText;
                    
                    // Mark as translated to avoid re-translation
                    if (node.parentElement) {
                      node.parentElement.setAttribute('data-translated', 'true');
                      node.parentElement.setAttribute('data-original-text', sourceText);
                    }
                  }
                  
                  // Add to processed nodes
                  processedNodes.current.add(node);
                  translationErrorCount.current = 0; // Reset error count on success
                  processedCount++;
                } catch (error) {
                  console.error('Translation error for text node:', error);
                  translationErrorCount.current++;
                  errorCount++;
                  
                  // If we're getting too many errors, show a toast and reduce batch size
                  if (translationErrorCount.current > 3) {
                    toast.error('Översättningsproblem. Vissa delar av sidan kommer att visas på originalspråket.', {
                      id: 'translation-error',
                      duration: 5000
                    });
                    if (maxNodesPerBatch > 2) {
                      maxNodesPerBatch = Math.max(2, Math.floor(maxNodesPerBatch / 2));
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error processing node:', error);
            errorCount++;
          }
        }));
        
        // Add a small delay between batches to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // After text nodes, translate key accessibility/display attributes
      const elementsWithAttrs = Array.from(document.querySelectorAll('[placeholder],[title],[aria-label],[alt]')) as HTMLElement[];
      const attrTasks: Array<{ el: HTMLElement; attr: string; value: string }> = [];
      
      elementsWithAttrs.forEach((el) => {
        // Skip elements inside do-not-translate containers
        let parent: HTMLElement | null = el;
        let skip = false;
        while (parent) {
          if (parent.hasAttribute('data-do-not-translate') && parent.getAttribute('data-do-not-translate') === 'true') {
            skip = true;
            break;
          }
          parent = parent.parentElement;
        }
        if (skip) return;
        
        ATTRS_TO_TRANSLATE.forEach((attr) => {
          const val = el.getAttribute(attr);
          if (!val || val.trim().length <= 1) return;
          if (el.getAttribute(`data-translated-attr-${attr}`) === 'true') return;
          attrTasks.push({ el, attr, value: val });
        });
      });
      
      while (attrTasks.length > 0) {
        const batch = attrTasks.splice(0, maxNodesPerBatch);
        await Promise.all(batch.map(async ({ el, attr, value }) => {
          try {
            const originalAttr = (el.getAttribute(`data-original-${attr}`) || value).trim();
            const translated = await translateRef.current(originalAttr, force);
            // Stale guard: only apply if generation and language still match
            if (generationRef.current !== genAtStart || currentLanguage !== langAtStart) {
              return;
            }
            if (translated && translated !== originalAttr) {
              el.setAttribute(attr, translated);
              el.setAttribute(`data-translated-attr-${attr}`, 'true');
              el.setAttribute(`data-original-${attr}`, originalAttr);
              processedCount++;
            }
          } catch (e) {
            console.error(`Translation error for attribute ${attr}:`, e);
            translationErrorCount.current++;
            errorCount++;
            if (translationErrorCount.current > 3 && maxNodesPerBatch > 2) {
              maxNodesPerBatch = Math.max(2, Math.floor(maxNodesPerBatch / 2));
            }
          }
        }));
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      if (window.isTranslatingFullPage) {
        if (errorCount > 0) {
          toast.info(`Översättning slutförd med ${errorCount} fel. Vissa delar kan visas på originalspråket.`, {
            id: 'translation-complete-with-errors',
            duration: 3000
          });
        } else {
          toast.success('Översättning slutförd!', {
            id: 'translation-complete',
            duration: 3000
          });
        }
        window.isTranslatingFullPage = false;
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Ett fel uppstod vid översättning.', {
        id: 'translation-general-error',
        duration: 5000
      });
    } finally {
      setIsTranslating(false);
    }
  };

  // Listen for manual force-translate events
  useEffect(() => {
    const handler = () => {
      if (disabled || currentLanguage === originalLanguage) return;
      window.isTranslatingFullPage = true;
      const force = !staticOnlyMode; // Only force API calls when not in static-only
      translateFullPage(force);
    };
    window.addEventListener('force-translate', handler as EventListener);
    return () => window.removeEventListener('force-translate', handler as EventListener);
  }, [currentLanguage, originalLanguage, staticOnlyMode, disabled]);

  // Function to dynamically translate text nodes that aren't marked as "do-not-translate"
  useEffect(() => {
    // Skip if disabled or using the original language
    if (disabled || currentLanguage === originalLanguage) {
      return;
    }
 
    const translateVisibleText = async () => {
      // Don't run regular translation if full page translation is in progress
      if (window.isTranslatingFullPage) {
        return;
      }

      try {
        await translateFullPage();
      } catch (error) {
        console.error('Error in translateVisibleText:', error);
      }
    };

    // Initial translation (delayed to allow important content to render first)
    const initialTranslationTimeout = setTimeout(() => {
      translateVisibleText();
    }, 1000);
    
    // Set up MutationObserver to handle dynamically added/changed content
    const observer = new MutationObserver((mutations) => {
      let needsTranslation = false;

      for (const mutation of mutations) {
        // New nodes added -> translate
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          needsTranslation = true;
          continue;
        }

        // Text content changed -> if it was translated before, reset markers so it can be re-translated
        if (mutation.type === 'characterData') {
          const targetNode = mutation.target as CharacterData;
          const parent = targetNode.parentElement as HTMLElement | null;
          if (parent && parent.getAttribute('data-translated') === 'true') {
            const currentText = (parent.textContent || '').trim();
            const original = (parent.getAttribute('data-original-text') || '').trim();
            if (currentText && currentText !== original) {
              parent.removeAttribute('data-translated');
              parent.removeAttribute('data-original-text');
              // Allow this text node to be processed again
              processedNodes.current.delete(targetNode);
              needsTranslation = true;
            }
          }
          continue;
        }

        // Attribute value changed -> if it was translated before, reset marker for that attribute
        if (mutation.type === 'attributes' && mutation.attributeName) {
          const attr = mutation.attributeName;
          const el = mutation.target as HTMLElement;
          if ((ATTRS_TO_TRANSLATE as readonly string[]).includes(attr)) {
            const currentVal = (el.getAttribute(attr) || '').trim();
            const originalAttr = (el.getAttribute(`data-original-${attr}`) || '').trim();
            if (currentVal && currentVal !== originalAttr) {
              el.removeAttribute(`data-translated-attr-${attr}`);
              el.removeAttribute(`data-original-${attr}`);
              needsTranslation = true;
            }
          }
        }
      }
      
      if (needsTranslation) {
        // Debounce translation to avoid excessive calls
        if (window.translationTimeout) {
          clearTimeout(window.translationTimeout);
        }
        
        window.translationTimeout = setTimeout(() => {
          translateVisibleText();
        }, 800); // Slightly shorter delay for snappier updates
      }
    });
    
    // Start observing the document
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['placeholder','title','aria-label','alt'] 
    });
    
    // Cleanup function
    return () => {
      observer.disconnect();
      clearTimeout(initialTranslationTimeout);
      if (window.translationTimeout) {
        clearTimeout(window.translationTimeout);
      }
    };
  }, [currentLanguage, originalLanguage, disabled]);

  return (
    <>
      {staticOnlyMode && currentLanguage !== originalLanguage && (
        <div className="fixed bottom-4 left-4 z-50">
          <Badge variant="secondary">Statisk översättning</Badge>
        </div>
      )}
      {isTranslating && (
        <div className="fixed bottom-4 right-4 bg-primary text-white px-4 py-2 rounded-md shadow-md z-50 flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
          <span>Översätter...</span>
        </div>
      )}
      {typeof window !== 'undefined' && window.localStorage.getItem('translate_debug') === '1' && (
        <div className="fixed bottom-16 left-4 z-50 flex gap-2">
          <Button size="sm" variant="secondary" onClick={async () => {
            try {
              const res = await translateRef.current('Hej', true);
              toast.success(`Självtest OK: "Hej" → "${res}"`);
            } catch (e: any) {
              toast.error(`Självtest fel: ${e?.message || 'okänt fel'}`);
            }
          }}>
            Kör självtest
          </Button>
        </div>
      )}
    </>
  );
};

export default PageObserver;
